/**
 * Shared Filter Configuration Types and Options
 * 
 * This module provides type-safe filter configurations derived from shared types
 * to prevent drift between frontend and backend filter options.
 */

// ============================================================================
// Stream Status Filter
// ============================================================================

/**
 * Stream status values from SharedStream type
 */
export const STREAM_STATUS_VALUES = ['scheduled', 'live', 'ended'] as const;
export type StreamStatusValue = typeof STREAM_STATUS_VALUES[number];

/**
 * Stream status filter options for UI components
 */
export const STREAM_STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'live', label: 'Live' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'ended', label: 'Ended' },
] as const;

/**
 * Stream status filter options without 'all' (for API queries)
 */
export const STREAM_STATUS_API_OPTIONS = [
  { value: 'live', label: 'Live' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'ended', label: 'Ended' },
] as const;

// ============================================================================
// User Status Filter
// ============================================================================

/**
 * User status values from SharedUser type
 */
export const USER_STATUS_VALUES = ['active', 'pending', 'banned'] as const;
export type UserStatusValue = typeof USER_STATUS_VALUES[number];

/**
 * User status filter options for UI components
 */
export const USER_STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'banned', label: 'Banned' },
] as const;

// ============================================================================
// Role Filter
// ============================================================================

/**
 * Role values from the role constants
 */
export const ROLE_VALUES = ['member', 'pastor', 'media_team', 'admin'] as const;
export type RoleValue = typeof ROLE_VALUES[number];

/**
 * Role filter options for UI components
 */
export const ROLE_OPTIONS = [
  { value: 'all', label: 'All Roles' },
  { value: 'admin', label: 'Admin' },
  { value: 'media_team', label: 'Media Team' },
  { value: 'pastor', label: 'Pastor' },
  { value: 'member', label: 'Member' },
] as const;

// ============================================================================
// Comment Status Filter
// ============================================================================

/**
 * Comment moderation status
 */
export const COMMENT_STATUS_VALUES = ['pending', 'approved', 'rejected'] as const;
export type CommentStatusValue = typeof COMMENT_STATUS_VALUES[number];

/**
 * Comment status filter options for UI components
 */
export const COMMENT_STATUS_OPTIONS = [
  { value: 'all', label: 'All Comments' },
  { value: 'pending', label: 'Pending Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
] as const;

// ============================================================================
// Invite Status Filter
// ============================================================================

/**
 * Invite status values from SharedInviteCode type
 */
export const INVITE_STATUS_VALUES = ['active', 'used', 'expired'] as const;
export type InviteStatusValue = typeof INVITE_STATUS_VALUES[number];

/**
 * Invite status filter options for UI components
 */
export const INVITE_STATUS_OPTIONS = [
  { value: 'all', label: 'All Invites' },
  { value: 'active', label: 'Active' },
  { value: 'used', label: 'Used' },
  { value: 'expired', label: 'Expired' },
] as const;

// ============================================================================
// Notification Type Filter
// ============================================================================

/**
 * Notification type values from SharedNotification type
 */
export const NOTIFICATION_TYPE_VALUES = [
  'stream_live',
  'stream_ended',
  'comment',
  'mention',
  'like',
  'invite',
  'access_granted',
  'access_revoked',
  'system',
] as const;
export type NotificationTypeValue = typeof NOTIFICATION_TYPE_VALUES[number];

/**
 * Notification type filter options for UI components
 */
export const NOTIFICATION_TYPE_OPTIONS = [
  { value: 'all', label: 'All Notifications' },
  { value: 'stream_live', label: 'Stream Live' },
  { value: 'stream_ended', label: 'Stream Ended' },
  { value: 'comment', label: 'Comments' },
  { value: 'mention', label: 'Mentions' },
  { value: 'like', label: 'Likes' },
  { value: 'invite', label: 'Invites' },
  { value: 'access_granted', label: 'Access Granted' },
  { value: 'access_revoked', label: 'Access Revoked' },
  { value: 'system', label: 'System' },
] as const;

// ============================================================================
// Audit Log Filters
// ============================================================================

/**
 * Audit severity values
 */
export const AUDIT_SEVERITY_VALUES = ['low', 'medium', 'high', 'critical'] as const;
export type AuditSeverityValue = typeof AUDIT_SEVERITY_VALUES[number];

/**
 * Audit severity filter options for UI components
 */
export const AUDIT_SEVERITY_OPTIONS = [
  { value: 'all', label: 'All Severities' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
] as const;

// ============================================================================
// Recording Status Filter
// ============================================================================

/**
 * Recording status values
 */
export const RECORDING_STATUS_VALUES = ['processing', 'ready', 'failed'] as const;
export type RecordingStatusValue = typeof RECORDING_STATUS_VALUES[number];

/**
 * Recording status filter options for UI components
 */
export const RECORDING_STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'ready', label: 'Ready' },
  { value: 'processing', label: 'Processing' },
  { value: 'failed', label: 'Failed' },
] as const;

// ============================================================================
// Filter Configuration Types
// ============================================================================

/**
 * Filter option type for select/multiselect components
 */
export interface FilterOption {
  value: string;
  label: string;
}

/**
 * Filter type enum
 */
export type FilterType = 'select' | 'multiselect' | 'date-range' | 'search' | 'checkbox';

/**
 * Base filter configuration
 */
export interface BaseFilterConfig {
  id: string;
  label: string;
  type: FilterType;
  placeholder?: string;
  defaultValue?: string | string[];
}

/**
 * Select filter configuration
 */
export interface SelectFilterConfig extends BaseFilterConfig {
  type: 'select';
  options: readonly FilterOption[];
}

/**
 * Multiselect filter configuration
 */
export interface MultiselectFilterConfig extends BaseFilterConfig {
  type: 'multiselect';
  options: readonly FilterOption[];
}

/**
 * Search filter configuration
 */
export interface SearchFilterConfig extends BaseFilterConfig {
  type: 'search';
}

/**
 * Date range filter configuration
 */
export interface DateRangeFilterConfig extends BaseFilterConfig {
  type: 'date-range';
}

/**
 * Checkbox filter configuration
 */
export interface CheckboxFilterConfig extends BaseFilterConfig {
  type: 'checkbox';
}

/**
 * Union type for all filter configurations
 */
export type FilterConfig =
  | SelectFilterConfig
  | MultiselectFilterConfig
  | SearchFilterConfig
  | DateRangeFilterConfig
  | CheckboxFilterConfig;

// ============================================================================
// Pre-built Filter Configurations
// ============================================================================

/**
 * Common stream filters configuration
 */
export const STREAM_FILTERS: FilterConfig[] = [
  {
    id: 'searchQuery',
    label: 'Search',
    type: 'search',
    placeholder: 'Search streams...',
  },
  {
    id: 'status',
    label: 'Status',
    type: 'select',
    options: STREAM_STATUS_OPTIONS,
    placeholder: 'All Statuses',
  },
];

/**
 * Admin stream filters configuration (includes more options)
 */
export const ADMIN_STREAM_FILTERS: FilterConfig[] = [
  {
    id: 'search',
    label: 'Search',
    type: 'search',
    placeholder: 'Search streams...',
  },
  {
    id: 'status',
    label: 'Status',
    type: 'select',
    options: STREAM_STATUS_OPTIONS,
    placeholder: 'All Status',
  },
];

/**
 * User management filters configuration
 */
export const USER_FILTERS: FilterConfig[] = [
  {
    id: 'search',
    label: 'Search',
    type: 'search',
    placeholder: 'Search users by name or email...',
  },
  {
    id: 'role',
    label: 'Role',
    type: 'select',
    options: ROLE_OPTIONS,
    placeholder: 'All Roles',
  },
  {
    id: 'status',
    label: 'Status',
    type: 'select',
    options: USER_STATUS_OPTIONS,
    placeholder: 'All Statuses',
  },
];

/**
 * Comment moderation filters configuration
 */
export const COMMENT_FILTERS: FilterConfig[] = [
  {
    id: 'search',
    label: 'Search',
    type: 'search',
    placeholder: 'Search comments...',
  },
  {
    id: 'status',
    label: 'Status',
    type: 'select',
    options: COMMENT_STATUS_OPTIONS,
    placeholder: 'All Comments',
  },
];

/**
 * Recording management filters configuration
 */
export const RECORDING_FILTERS: FilterConfig[] = [
  {
    id: 'search',
    label: 'Search',
    type: 'search',
    placeholder: 'Search recordings...',
  },
  {
    id: 'status',
    label: 'Status',
    type: 'select',
    options: RECORDING_STATUS_OPTIONS,
    placeholder: 'All Status',
  },
];

// ============================================================================
// Moderation Filters
// ============================================================================

/**
 * Moderation type options
 */
export const MODERATION_TYPE_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'comment', label: 'Comments' },
  { value: 'user', label: 'Users' },
  { value: 'stream', label: 'Streams' },
] as const;

/**
 * Moderation status options (extended for moderation queue)
 */
export const MODERATION_STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'flagged', label: 'Flagged' },
] as const;

/**
 * Priority options for moderation
 */
export const PRIORITY_OPTIONS = [
  { value: 'all', label: 'All Priority' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
] as const;

/**
 * Date range options for filtering
 */
export const DATE_RANGE_OPTIONS = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
] as const;

/**
 * Moderation queue filters configuration
 */
export const MODERATION_FILTERS: FilterConfig[] = [
  {
    id: 'search',
    label: 'Search',
    type: 'search',
    placeholder: 'Search by content or author...',
  },
  {
    id: 'type',
    label: 'Type',
    type: 'select',
    options: MODERATION_TYPE_OPTIONS,
    placeholder: 'All Types',
  },
  {
    id: 'status',
    label: 'Status',
    type: 'select',
    options: MODERATION_STATUS_OPTIONS,
    placeholder: 'All Status',
  },
  {
    id: 'priority',
    label: 'Priority',
    type: 'select',
    options: PRIORITY_OPTIONS,
    placeholder: 'All Priority',
  },
  {
    id: 'dateRange',
    label: 'Date Range',
    type: 'select',
    options: DATE_RANGE_OPTIONS,
    placeholder: 'All Time',
  },
];

/**
 * Invite management filters configuration
 */
export const INVITE_FILTERS: FilterConfig[] = [
  {
    id: 'search',
    label: 'Search',
    type: 'search',
    placeholder: 'Search by code...',
  },
  {
    id: 'status',
    label: 'Status',
    type: 'select',
    options: INVITE_STATUS_OPTIONS,
    placeholder: 'All Invites',
  },
];

/**
 * Audit event type options
 */
export const AUDIT_EVENT_TYPE_OPTIONS = [
  { value: 'all', label: 'All Events' },
  { value: 'AUTH_LOGIN', label: 'Login' },
  { value: 'AUTH_LOGOUT', label: 'Logout' },
  { value: 'AUTH_FAILED_LOGIN', label: 'Failed Login' },
  { value: 'ADMIN_USER_CREATE', label: 'Create User' },
  { value: 'ADMIN_USER_UPDATE', label: 'Update User' },
  { value: 'ADMIN_USER_DELETE', label: 'Delete User' },
  { value: 'ADMIN_USER_BAN', label: 'Ban User' },
  { value: 'ADMIN_USER_UNBAN', label: 'Unban User' },
  { value: 'ADMIN_STREAM_CREATE', label: 'Create Stream' },
  { value: 'ADMIN_STREAM_UPDATE', label: 'Update Stream' },
  { value: 'ADMIN_STREAM_DELETE', label: 'Delete Stream' },
  { value: 'ADMIN_CONTENT_MODERATE', label: 'Moderate Content' },
  { value: 'STREAM_STARTED', label: 'Stream Started' },
  { value: 'STREAM_ENDED', label: 'Stream Ended' },
  { value: 'COMMENT_POSTED', label: 'Comment Posted' },
  { value: 'COMMENT_DELETED', label: 'Comment Deleted' },
] as const;

/**
 * Audit resource type options
 */
export const AUDIT_RESOURCE_TYPE_OPTIONS = [
  { value: 'all', label: 'All Resources' },
  { value: 'USER', label: 'User' },
  { value: 'STREAM', label: 'Stream' },
  { value: 'COMMENT', label: 'Comment' },
  { value: 'SYSTEM', label: 'System' },
  { value: 'SETTING', label: 'Setting' },
  { value: 'POLL', label: 'Poll' },
] as const;

/**
 * Audit log filters configuration
 */
export const AUDIT_LOG_FILTERS: FilterConfig[] = [
  {
    id: 'search',
    label: 'Search',
    type: 'search',
    placeholder: 'Search audit logs...',
  },
  {
    id: 'dateRange',
    label: 'Date Range',
    type: 'date-range',
  },
  {
    id: 'eventType',
    label: 'Event Type',
    type: 'select',
    options: AUDIT_EVENT_TYPE_OPTIONS,
    placeholder: 'All Event Types',
  },
  {
    id: 'resourceType',
    label: 'Resource Type',
    type: 'select',
    options: AUDIT_RESOURCE_TYPE_OPTIONS,
    placeholder: 'All Resources',
  },
  {
    id: 'severity',
    label: 'Severity',
    type: 'select',
    options: AUDIT_SEVERITY_OPTIONS,
    placeholder: 'All Levels',
  },
];

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if a filter value is the "all" option
 */
export function isAllOption(value: string | undefined): boolean {
  return value === 'all' || value === '' || value === undefined;
}

/**
 * Convert filter value for API query (removes 'all' values)
 */
export function filterValueForApi(value: string | string[] | undefined): string | string[] | undefined {
  if (value === undefined || value === '') {
    return undefined;
  }
  if (typeof value === 'string') {
    return isAllOption(value) ? undefined : value;
  }
  if (Array.isArray(value)) {
    const filtered = value.filter(v => !isAllOption(v));
    return filtered.length > 0 ? filtered : undefined;
  }
  return value;
}

/**
 * Build API query params from filter state
 */
export function buildFilterParams(filters: Record<string, string | string[]>): URLSearchParams {
  const params = new URLSearchParams();
  
  Object.entries(filters).forEach(([key, value]) => {
    const apiValue = filterValueForApi(value);
    if (apiValue !== undefined) {
      if (Array.isArray(apiValue)) {
        params.set(key, apiValue.join(','));
      } else {
        params.set(key, apiValue);
      }
    }
  });
  
  return params;
}
